/**
 * LanguageContext — Global language state for the app.
 *
 * Exposes the currently selected language code and a setter.
 * Persists language preference in localStorage.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const STORAGE_KEY = 'appLanguage';

interface LanguageContextValue {
    /** Currently active language code ('en' = default) */
    activeLanguage: string;
    /** Change the global language — persists to localStorage */
    setLanguage: (langCode: string) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activeLanguage, setActiveLanguage] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) || 'en';
        } catch {
            return 'en';
        }
    });

    const setLanguage = useCallback((langCode: string) => {
        setActiveLanguage(langCode);
        try {
            localStorage.setItem(STORAGE_KEY, langCode);
        } catch {
            // localStorage unavailable — state still updates in memory
        }
    }, []);

    return (
        <LanguageContext.Provider value={{ activeLanguage, setLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};

export function useLanguage(): LanguageContextValue {
    const ctx = useContext(LanguageContext);
    if (!ctx) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return ctx;
}
